import { useEffect } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  clearAllNotifications,
  deleteNotification,
  getNotifications,
  getUnreadCount,
  markAllAsRead,
  markAsRead,
  type Notification,
} from '../../services/notifications/notificationService';
import { supabase } from '../../lib/supabaseClient';
import { useAuthStore } from '../../store/useAuthStore';
import { POLL_2MIN } from '../../lib/queryPresets';

export const notificationKeys = {
  all: ['notifications'] as const,
  list: () => [...notificationKeys.all, 'list'] as const,
  unreadCount: () => [...notificationKeys.all, 'unread-count'] as const,
};

export function useNotifications(limit = 50) {
  return useQuery({
    ...POLL_2MIN, // Realtime notifications retiré de la publication (avr. 2026)
    queryKey: notificationKeys.list(),
    queryFn: () => getNotifications(limit),
  });
}

export function useUnreadNotificationCount() {
  return useQuery({
    ...POLL_2MIN,
    queryKey: notificationKeys.unreadCount(),
    queryFn: getUnreadCount,
  });
}

export function useMarkNotificationAsRead() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: markAsRead,
    onMutate: async (id) => {
      await queryClient.cancelQueries({ queryKey: notificationKeys.all });
      const prevList = queryClient.getQueryData<Notification[]>(notificationKeys.list());
      const prevCount = queryClient.getQueryData<number>(notificationKeys.unreadCount());

      if (prevList) {
        queryClient.setQueryData<Notification[]>(
          notificationKeys.list(),
          prevList.map((n) =>
            n.id === id ? { ...n, is_read: true, read_at: new Date().toISOString() } : n,
          ),
        );
      }
      if (typeof prevCount === 'number') {
        const wasUnread = prevList?.find((n) => n.id === id)?.is_read === false;
        if (wasUnread) {
          queryClient.setQueryData<number>(
            notificationKeys.unreadCount(),
            Math.max(0, prevCount - 1),
          );
        }
      }

      return { prevList, prevCount };
    },
    onError: (_err, _id, ctx) => {
      if (ctx?.prevList) queryClient.setQueryData(notificationKeys.list(), ctx.prevList);
      if (typeof ctx?.prevCount === 'number')
        queryClient.setQueryData(notificationKeys.unreadCount(), ctx.prevCount);
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: notificationKeys.all });
    },
  });
}

export function useMarkAllNotificationsAsRead() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: markAllAsRead,
    onMutate: async () => {
      await queryClient.cancelQueries({ queryKey: notificationKeys.all });
      const prevList = queryClient.getQueryData<Notification[]>(notificationKeys.list());
      const prevCount = queryClient.getQueryData<number>(notificationKeys.unreadCount());

      if (prevList) {
        const now = new Date().toISOString();
        queryClient.setQueryData<Notification[]>(
          notificationKeys.list(),
          prevList.map((n) => ({ ...n, is_read: true, read_at: n.read_at ?? now })),
        );
      }
      queryClient.setQueryData<number>(notificationKeys.unreadCount(), 0);

      return { prevList, prevCount };
    },
    onError: (_err, _id, ctx) => {
      if (ctx?.prevList) queryClient.setQueryData(notificationKeys.list(), ctx.prevList);
      if (typeof ctx?.prevCount === 'number')
        queryClient.setQueryData(notificationKeys.unreadCount(), ctx.prevCount);
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: notificationKeys.all });
    },
  });
}

export function useDeleteNotification() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: deleteNotification,
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: notificationKeys.all });
    },
  });
}

export function useClearAllNotifications() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: clearAllNotifications,
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: notificationKeys.all });
    },
  });
}

export function useRealtimeNotifications() {
  const queryClient = useQueryClient();
  const collaboratorId = useAuthStore((s) => s.collaborator?.id);

  useEffect(() => {
    if (!collaboratorId) return;

    const channel = supabase
      .channel(`notifications-realtime-${collaboratorId}`)
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'notifications',
          filter: `recipient_id=eq.${collaboratorId}`,
        },
        (payload) => {
          void queryClient.invalidateQueries({ queryKey: notificationKeys.all });
          const type = (payload.new as { type?: string } | null)?.type;
          if (typeof type === 'string' && type.startsWith('signature_')) {
            void queryClient.invalidateQueries({ queryKey: ['signature'] });
          }
        },
      )
      .subscribe();

    return () => { void supabase.removeChannel(channel); };
  }, [collaboratorId, queryClient]);
}
